import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { API } from "../api";
import { Loader2, BadgeCheck, Ban, Download, ShieldCheck } from "lucide-react";

export default function PassPage() {
  const { code } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/pass/${code}`).then(({ data }) => setP(data)).catch(() => setError("Pass not found")).finally(() => setLoading(false));
  }, [code]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0b1220]"><Loader2 className="animate-spin text-[#2ec9c9]" size={32} /></div>;
  if (error || !p) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0b1220] text-white p-6">
      <div className="text-center" data-testid="pass-not-found"><Ban className="mx-auto mb-3 text-red-400" size={40} /><p className="text-lg font-semibold">Pass not found</p><p className="text-white/60 text-sm mt-1">Check the code and try again.</p></div>
    </div>
  );

  const brand = p.brand_color || "#16243b";
  const valid = p.valid;
  const statusLabel = valid ? "VALID" : (p.expired ? "EXPIRED" : "REVOKED");
  const statusSub = valid
    ? "Free access to all live events"
    : (p.expired
        ? `This pass expired${p.expires_at ? ` on ${new Date(p.expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : ""}`
        : "This pass has been revoked");

  return (
    <div className="min-h-screen flex items-center justify-center p-5" style={{ background: "#0b1220" }} data-testid="pass-page">
      <div className="w-full max-w-md">
        <div className="rounded-3xl overflow-hidden shadow-2xl" style={{ background: "#131c2e" }}>
          <div className="p-6 flex items-center gap-3" style={{ background: brand }}>
            {p.logo_url ? <img src={p.logo_url} alt="" className="h-9" /> : <ShieldCheck className="text-white" size={26} />}
            <div><p className="text-white font-semibold leading-tight">{p.business_name}</p><p className="text-white/70 text-xs">Access Pass</p></div>
          </div>

          <div className={`px-6 py-8 text-center ${valid ? "" : ""}`}>
            <div className={`mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-4 ${valid ? "bg-green-500/15" : "bg-red-500/15"}`}>
              {valid ? <BadgeCheck className="text-green-400" size={44} data-testid="pass-valid-icon" /> : <Ban className="text-red-400" size={44} data-testid="pass-revoked-icon" />}
            </div>
            <p className={`text-2xl font-extrabold tracking-tight ${valid ? "text-green-400" : "text-red-400"}`} data-testid="pass-status">
              {statusLabel}
            </p>
            <span className="inline-block mt-3 text-sm font-semibold px-4 py-1.5 rounded-full bg-[#2ec9c9]/15 text-[#2ec9c9]" data-testid="pass-type">{p.pass_type}</span>
            <p className="text-white text-lg font-semibold mt-4" data-testid="pass-holder">{p.holder_name}</p>
            <p className="text-white/60 text-sm mt-1">{statusSub}</p>
            {valid && p.expires_at && <p className="text-white/40 text-xs mt-1">Valid until {new Date(p.expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>}
            <p className="text-white/40 text-xs font-mono mt-4">{p.code}</p>
          </div>

          <div className="px-6 pb-6">
            <a href={`${API}/pass/${p.code}/pass.pdf`} data-testid="pass-download" className="w-full flex items-center justify-center gap-2 rounded-xl py-3 font-medium text-white transition-colors" style={{ background: brand }}>
              <Download size={17} /> Download / print pass
            </a>
            <p className="text-center text-white/40 text-xs mt-3">Present this screen or the QR at any door.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
